import { useState } from "react";
import type { MyCandidateFromAPI } from "../../types/candidate";
import { useCandidateData } from "../../hooks/useCandidateData";
import { toTitleCase } from "../../lib/utils";
import { yearsExpLabel } from "../candidates/candidate-utils";
import { env } from "../../lib/env";
import CandidateListModal from "../candidates/CandidateListModal";
import { Skeleton } from "../ui/Skeleton";
import ArrowRightLinear from "~icons/solar/arrow-right-linear";

const SHOW_SCORE = env.FEATURE_CONFIDENCE_SCORE_UI;

function initialsOf(name: string): string {
  return name.slice(0, 2).toUpperCase();
}

/** Footer meta on a card: v3 `cand.location || cand.experience`. */
function metaFor(c: MyCandidateFromAPI): string {
  if (c.location) return c.location;
  const exp = yearsExpLabel(c.years_of_experience);
  if (exp) return exp;
  const score = c.confidence_score?.overall_score;
  if (SHOW_SCORE && typeof score === "number") return `${Math.round(score)}% match`;
  return "";
}

/** Second line on a card: v3 `cand.role`. */
function roleFor(c: MyCandidateFromAPI): string {
  if (c.current_job_title) return toTitleCase(c.current_job_title);
  if (c.current_company) return toTitleCase(c.current_company);
  return c.email ?? "No email on file";
}

interface CandidateCardsProps {
  jdPath: string;
}

/**
 * Top-4 candidate cards inside the assistant bubble plus a footer with the
 * remaining count and "View all (N)" — v3 `m.candidates` block.
 */
export function CandidateCards({ jdPath }: CandidateCardsProps) {
  const { candidates, total, isLoading } = useCandidateData(jdPath);
  const [modal, setModal] = useState<{
    open: boolean;
    initial: MyCandidateFromAPI | null;
  }>({ open: false, initial: null });

  if (isLoading) {
    return (
      <div className="mt-3.5 grid grid-cols-1 gap-2.5 sm:grid-cols-2" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[92px] rounded-xl" />
        ))}
      </div>
    );
  }
  if (candidates.length === 0) return null;

  const top = candidates.slice(0, 4);
  const openModal = (initial: MyCandidateFromAPI | null) =>
    setModal({ open: true, initial });

  return (
    <>
      <div className="mt-3.5 space-y-2.5">
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {top.map((c) => {
            const name = toTitleCase(c.full_name);
            const meta = metaFor(c);
            return (
              <button
                type="button"
                key={c.candidate_id}
                onClick={() => openModal(c)}
                className="group/card flex cursor-pointer flex-col justify-between rounded-xl border border-black/[0.08] bg-white/95 p-3 text-left shadow-xs transition-all duration-200 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2.5">
                    <div className="relative size-9 shrink-0 overflow-hidden rounded-full border border-black/10 bg-surface-3 shadow-xs">
                      {c.avatar_url ? (
                        <img src={c.avatar_url} alt="" className="size-full object-cover" />
                      ) : (
                        <div className="flex size-full items-center justify-center text-xs font-bold text-ink-muted">
                          {initialsOf(name)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold leading-tight text-ink transition-colors group-hover/card:text-primary sm:text-sm">
                        {name}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-ink-muted" title={roleFor(c)}>
                        {roleFor(c)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Meta & quick action footer */}
                <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-black/[0.04] pt-2 text-[11px]">
                  <span className="max-w-[120px] truncate text-ink-faint sm:max-w-[140px]" title={meta}>
                    {meta}
                  </span>
                  <span className="flex shrink-0 items-center gap-1 font-medium text-primary transition-transform group-hover/card:translate-x-0.5">
                    <span>Profile</span>
                    <ArrowRightLinear aria-hidden="true" className="size-3" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer with candidate count & "View all" */}
        <div className="flex items-center justify-between border-t border-black/[0.06] pt-2">
          <span className="truncate pr-2 text-xs font-normal text-ink-muted">
            {total > 4
              ? `...and ${total - 4} more candidates`
              : `${total} candidates found`}
          </span>
          <button
            type="button"
            onClick={() => openModal(null)}
            className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs transition-all duration-200 hover:scale-105 hover:bg-primary-hover active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <span>View all ({total})</span>
            <ArrowRightLinear aria-hidden="true" className="size-3.5" />
          </button>
        </div>
      </div>

      {/* Mounted only while open so each result message doesn't hold a dialog */}
      {modal.open && (
        <CandidateListModal
          jdPath={jdPath}
          initialCandidate={modal.initial}
          onClose={() => setModal({ open: false, initial: null })}
        />
      )}
    </>
  );
}
